import React, { memo, useCallback, useRef } from 'react'

import {
  Bell, classnames, Icon, Usage,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, string } from 'prop-types'

import { iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { SETTINGS_MODAL_TAB } from 'Pods/ThreeDStudio/Store/constants'

const NAV_ITEMS = [
  { tab: SETTINGS_MODAL_TAB.USAGE, labelKey: 'settingsModal.tabs.usage', icon: Usage },
  { tab: SETTINGS_MODAL_TAB.NOTIFICATIONS, labelKey: 'settingsModal.tabs.notifications', icon: Bell },
]

// The nav is a column on desktop and a row below xs, so both axes move between tabs.
const ARROW_STEP = {
  ArrowUp: -1,
  ArrowLeft: -1,
  ArrowDown: 1,
  ArrowRight: 1,
}

export const tabDomId = tab => `settings-modal-tab-${tab}`
export const panelDomId = tab => `settings-modal-panel-${tab}`

function Nav({ activeTab, onSelect }) {
  const t = useTranslation()
  const tabRefs = useRef({})

  const selectAndFocus = useCallback((tab) => {
    onSelect(tab)
    tabRefs.current[tab]?.focus()
  }, [onSelect])

  const handleKeyDown = useCallback((event) => {
    const step = ARROW_STEP[event.key]
    const isEdgeKey = event.key === 'Home' || event.key === 'End'
    if (!step && !isEdgeKey) return

    event.preventDefault()

    if (isEdgeKey) {
      selectAndFocus(NAV_ITEMS[event.key === 'Home' ? 0 : NAV_ITEMS.length - 1].tab)
      return
    }

    const activeIndex = NAV_ITEMS.findIndex(item => item.tab === activeTab)
    const nextIndex = (activeIndex + step + NAV_ITEMS.length) % NAV_ITEMS.length
    selectAndFocus(NAV_ITEMS[nextIndex].tab)
  }, [activeTab, selectAndFocus])

  return (
    <div
      className="settings-modal__nav"
      role="tablist"
      aria-label={t('settingsModal.title')}
    >
      {NAV_ITEMS.map(({ tab, labelKey, icon: NavIcon }) => {
        const isActive = activeTab === tab

        return (
          <button
            key={tab}
            ref={(node) => { tabRefs.current[tab] = node }}
            type="button"
            role="tab"
            id={tabDomId(tab)}
            aria-selected={isActive}
            aria-controls={panelDomId(tab)}
            // Roving tabindex: the tablist is a single tab stop and the arrow keys
            // move between the tabs inside it.
            tabIndex={isActive ? 0 : -1}
            className={classnames(
              'settings-modal__nav-item',
              isActive && 'settings-modal__nav-item--active',
            )}
            onClick={() => onSelect(tab)}
            onKeyDown={handleKeyDown}
          >
            <span className="settings-modal__nav-icon" aria-hidden="true">
              <Icon svgComponent={NavIcon} color="darker" size={iconSizeMedium} notSpaced />
            </span>
            <span className="cgt-body-md-semibold">{t(labelKey)}</span>
          </button>
        )
      })}
    </div>
  )
}

Nav.propTypes = {
  activeTab: string.isRequired,
  onSelect: func.isRequired,
}

export default memo(Nav)
